import React from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
const pollsItems = [{
  label: 'Random feedback',
  detail: 'Fans react without much context'
}, {
  label: 'Low commitment',
  detail: 'Anyone can answer and move on'
}, {
  label: 'No creator follow-through',
  detail: 'Results can disappear into the ether'
}, {
  label: 'Little payoff after the vote',
  detail: 'The interaction usually ends there'
}];
const pulseItems = [{
  label: 'Goal-based decisions',
  detail: 'Fans weigh in within the context of a real goal'
}, {
  label: 'Real commitment',
  detail: 'Staking gives fans a reason to stay invested'
}, {
  label: 'Resolved in the real world',
  detail: 'Creators are expected to follow through to an outcome'
}, {
  label: 'Built-in content',
  detail: 'The market playing out becomes the content'
}];
export function WhyPollsFail() {
  return <section className="py-28 lg:py-40 px-6 bg-navy-950 relative overflow-hidden" data-id="element-225">
      {/* Subtle background accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-mint-500/[0.03] to-wave-500/[0.03] rounded-full blur-[100px] -z-10" data-id="element-226" />

      <div className="max-w-5xl mx-auto" data-id="element-227">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-228">
          <h2 className="text-4xl lg:text-6xl font-extrabold text-white mb-4" data-id="element-229">
            Built for real participation.
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-230">
            Creators who are serious about fan engagement are choosing Pulse
            Markets.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="grid md:grid-cols-[1fr,auto,1fr] gap-0 items-stretch" data-id="element-231">
          {/* Polls Column */}
          <div className="rounded-2xl md:rounded-r-none border border-slate-800/50 bg-slate-900/20 p-8 lg:p-10 relative" data-id="element-232">
            <div className="flex items-center gap-3 mb-10" data-id="element-233">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/15 flex items-center justify-center" data-id="element-234">
                <XIcon className="w-5 h-5 text-red-400/80" data-id="element-235" />
              </div>
              <div data-id="element-236">
                <h3 className="text-lg font-bold text-slate-300" data-id="element-237">
                  Polls & Comments
                </h3>
                <p className="text-xs text-slate-500 font-medium" data-id="element-238">
                  The old way
                </p>
              </div>
            </div>
            <div className="space-y-6" data-id="element-239">
              {pollsItems.map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-240">
                  <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-241">
                    <XIcon className="w-3 h-3 text-slate-500" data-id="element-242" />
                  </div>
                  <div data-id="element-243">
                    <div className="text-[15px] font-semibold text-slate-300" data-id="element-244">
                      {item.label}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-245">{item.detail}</div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Center divider */}
          <div className="hidden md:flex items-center justify-center w-16 relative" data-id="element-246">
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-slate-700 to-transparent" data-id="element-247" />
            <div className="relative w-10 h-10 rounded-full bg-navy-950 border border-slate-700 flex items-center justify-center z-10" data-id="element-248">
              <ArrowRightIcon className="w-4 h-4 text-mint-400" data-id="element-249" />
            </div>
          </div>

          {/* Pulse Markets Column */}
          <div className="rounded-2xl md:rounded-l-none border border-mint-500/20 bg-gradient-to-br from-mint-500/[0.06] via-navy-950 to-wave-500/[0.04] p-8 lg:p-10 relative overflow-hidden" data-id="element-250">
            {/* Corner glow */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-mint-500/10 rounded-full blur-3xl" data-id="element-251" />

            <div className="flex items-center gap-3 mb-10 relative" data-id="element-252">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-mint-500/20 to-wave-500/15 border border-mint-500/25 flex items-center justify-center" data-id="element-253">
                <CheckIcon className="w-5 h-5 text-mint-400" data-id="element-254" />
              </div>
              <div data-id="element-255">
                <h3 className="text-lg font-bold text-white" data-id="element-256">Pulse Markets</h3>
                <p className="text-xs text-mint-400/70 font-semibold" data-id="element-257">
                  The upgrade
                </p>
              </div>
            </div>
            <div className="space-y-6 relative" data-id="element-258">
              {pulseItems.map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-259">
                  <div className="w-5 h-5 rounded-full bg-mint-500/15 border border-mint-500/25 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-260">
                    <CheckIcon className="w-3 h-3 text-mint-400" data-id="element-261" />
                  </div>
                  <div data-id="element-262">
                    <div className="text-[15px] font-semibold text-white" data-id="element-263">
                      {item.label}
                    </div>
                    <div className="text-sm text-slate-400" data-id="element-264">{item.detail}</div>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}